import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  getCantileverDocumentEntryId,
  getSystemDocumentBqId,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { isCmmsRecordId } from 'features/knowledgeDocumentViewer/workOrders/workOrder'
import { z } from 'zod'

import { isLikelyRolodexEntryId } from './documentEntryLookups'
import type { LegacyFavorites } from './documentFavoritesRepository'
import {
  type DocumentFavorite,
  type FavoriteReference,
  normalizeFavorites,
} from './documentFavoritesState'

const legacyFavoriteSchema = z.object({
  id: z.string().min(1),
  timestamp: z.number().int().nonnegative(),
  systemDocumentEntryId: z.string().min(1).optional(),
  bqDocumentId: z.string().min(1).optional(),
})

type LegacyFavoriteDocument = z.infer<typeof legacyFavoriteSchema>
export type FavoriteDocument = Pick<
  DocumentTableRow,
  | 'id'
  | 'title'
  | 'filename'
  | 'documentTitle'
  | 'bqDocumentId'
  | 'cantileverDocumentEntryId'
> & {
  reference: FavoriteReference
  systemDocumentEntryId: string
}

function indexStableDocumentIdsByEntry(rows: LegacyFavoriteDocument[]) {
  const stableIdByEntry = new Map<string, string>()
  for (const row of rows) {
    if (!row.systemDocumentEntryId) continue
    if (row.bqDocumentId) {
      stableIdByEntry.set(row.systemDocumentEntryId, row.bqDocumentId)
    } else if (!isCmmsRecordId(row.id) && !isLikelyRolodexEntryId(row.id)) {
      stableIdByEntry.set(row.systemDocumentEntryId, row.id)
    }
  }
  return stableIdByEntry
}

function getLegacyFavoriteReference(
  row: LegacyFavoriteDocument,
  stableIdByEntry: Map<string, string>
): FavoriteReference {
  if (isCmmsRecordId(row.id)) return { kind: 'cmms_record', entry_id: row.id }

  const documentId = row.bqDocumentId ?? stableIdByEntry.get(row.id)
  if (documentId) return { kind: 'document', document_id: documentId }
  if (isLikelyRolodexEntryId(row.id))
    return { kind: 'legacy_system_document', entry_id: row.id }
  return { kind: 'document', document_id: row.id }
}

export function importLegacyFavorites(rows: LegacyFavoriteDocument[]) {
  const stableIdByEntry = indexStableDocumentIdsByEntry(rows)
  return normalizeFavorites(
    rows.map((row) => ({
      reference: getLegacyFavoriteReference(row, stableIdByEntry),
      favorited_at: row.timestamp,
    }))
  )
}

export function createLegacyFavoritesSource(
  userId: string,
  siteId: string
): LegacyFavorites {
  const key = `gecko_favorite_documents:v1_${userId}_${siteId}`
  return {
    read: () => {
      try {
        const raw = localStorage.getItem(key)
        if (!raw) return null
        const rows = z.array(legacyFavoriteSchema).parse(JSON.parse(raw))
        const favorites = importLegacyFavorites(rows)
        return {
          favorites,
          acknowledge: () => {
            if (localStorage.getItem(key) === raw) localStorage.removeItem(key)
          },
        }
      } catch {
        // Leave malformed or inaccessible legacy storage untouched for recovery.
        return null
      }
    },
  }
}

export function favoriteReferenceMatchesRow(
  reference: FavoriteReference,
  row: Pick<DocumentTableRow, 'id' | 'systemEntryId' | 'bqDocumentId'>
) {
  if (reference.kind === 'document')
    return reference.document_id === (row.bqDocumentId ?? row.id)
  return (
    reference.entry_id ===
    (reference.kind === 'cmms_record' ? row.id : row.systemEntryId)
  )
}

export function resolveFavoritesForActiveDocuments({
  favoriteDocuments,
  activeDocuments,
  workOrderDocuments = [],
}: {
  favoriteDocuments: DocumentFavorite[]
  activeDocuments: SystemDocumentEntry[]
  workOrderDocuments?: DocumentTableRow[]
}): FavoriteDocument[] {
  const workOrdersById = new Map(workOrderDocuments.map((row) => [row.id, row]))
  const activeDocumentByBqId = new Map<string, SystemDocumentEntry>()
  const activeDocumentByEntryId = new Map<string, SystemDocumentEntry>()
  for (const entry of activeDocuments) {
    activeDocumentByEntryId.set(entry.entry.id, entry)
    const bqId = getSystemDocumentBqId(entry)
    if (bqId) activeDocumentByBqId.set(bqId, entry)
  }

  return favoriteDocuments.flatMap<FavoriteDocument>((favorite) => {
    const { reference } = favorite
    const workOrder =
      reference.kind === 'cmms_record'
        ? workOrdersById.get(reference.entry_id)
        : undefined
    if (workOrder)
      return [
        {
          reference,
          id: workOrder.id,
          title: workOrder.title,
          filename: workOrder.filename,
          documentTitle: workOrder.documentTitle,
          systemDocumentEntryId: workOrder.id,
        },
      ]
    const activeDocument =
      reference.kind === 'document'
        ? activeDocumentByBqId.get(reference.document_id)
        : reference.kind === 'legacy_system_document'
          ? activeDocumentByEntryId.get(reference.entry_id)
          : undefined
    if (!activeDocument) return []
    const bqDocumentId = getSystemDocumentBqId(activeDocument)
    if (!bqDocumentId) return []

    return [
      {
        reference,
        id: bqDocumentId,
        title: activeDocument.entry.data.title,
        filename: activeDocument.entry.data.filename,
        documentTitle:
          activeDocument.entry.data.document_title ??
          activeDocument.entry.data.title,
        systemDocumentEntryId: activeDocument.entry.id,
        bqDocumentId,
        cantileverDocumentEntryId: getCantileverDocumentEntryId(activeDocument),
      },
    ]
  })
}
